/**
 * Reload onto a genuinely fresh entry document (XT-413).
 *
 * A plain `location.reload()` re-fetches `/` from the same shared cache that
 * handed us the stale document in the first place. DigitalOcean's per-region
 * edge has been observed serving an `index.html` 46 h old — past its own
 * `s-maxage=86400` — naming asset files a later deploy deleted. That cache sits
 * upstream of Cloudflare and honours neither a client `no-cache` request header
 * nor a Cloudflare purge, so a bare reload loops on the same dead pointer.
 *
 * A query string IS outside DO's cache key, so reloading with one forces the
 * read through to object storage. The param is stripped from the address bar by
 * the inline bootstrap guard in `index.html` before the router boots, so it
 * never reaches route search-param validation.
 */

/** Must match `CACHE_BUST_PARAM` in the inline guard in `apps/web/index.html`. */
export const CACHE_BUST_PARAM = '__spa';

/**
 * Build the cache-busting URL for a reload. Exported for tests — callers
 * should use `reloadWithCacheBust`.
 */
export const buildCacheBustUrl = (href: string, nowMs: number): string => {
  const url = new URL(href);
  url.searchParams.set(CACHE_BUST_PARAM, String(nowMs));
  return url.toString();
};

/**
 * Replace the current document with a cache-busted fetch of the same URL.
 * `replace` rather than `assign` so the stale document does not stay in
 * session history as a back-button trap.
 */
export const reloadWithCacheBust = (): void => {
  try {
    window.location.replace(
      buildCacheBustUrl(window.location.href, Date.now())
    );
  } catch {
    // Malformed href (shouldn't happen) — a plain reload still beats nothing.
    window.location.reload();
  }
};
